import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cart, CartItem } from '../../../app/data/models/cart';
import api from '../../../app/api/api';
import { Order } from '../../../app/data/models/orders/order';
import Restaurant from '../../../app/data/models/restaurant';
import {
  updateCustomerOrder,
  updateCustomerRestaurant,
} from '../../../app/data/helpers/customerRestaurantHelpers';
import { replaceObjectById } from '../../../app/utils/array';
import { calculateCartTotals } from './cartHelper';
import { RootState } from '../../../app/store/configureStore';
import { fetchCustomerRestaurantAsync } from '../restaurant/customerRestaurantSlice';
import { fetchCachedRestaurantAsync } from '../restaurant/restaurantsCacheSlice';

interface CachedRestaurant {
  restaurant: Restaurant;
  timestamp: number; // Unix timestamp
}

export interface CartState {
  cart: Cart;
  orders: Order[];
  status: string;
  restaurant?: Restaurant;
}

const emptyCart = (restaurantId: string | undefined): Cart => ({
  restaurantName: undefined,
  restaurantId,
  orderItems: [],
  subtotal: 0,
  totalTax: 0,
  totalPrice: 0,
  totalQuantity: 0,
  defaultTaxRate: 0,
});

const loadCartFromStorage = (restaurantId: string | undefined): Cart => {
  if (!restaurantId) return emptyCart(undefined);

  const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes
  let priceIncludesTax = false;

  try {
    const savedRestaurant = localStorage.getItem(`restaurant_${restaurantId}`);
    if (savedRestaurant) {
      const cachedRestaurant: CachedRestaurant = JSON.parse(savedRestaurant);
      if (Date.now() - cachedRestaurant.timestamp < CACHE_EXPIRY_TIME) {
        priceIncludesTax =
          cachedRestaurant.restaurant.settings?.priceIncludesTax ?? false;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(`restaurant_${restaurantId}`);
      }
    }

    const savedCart = localStorage.getItem(`cart_${restaurantId}`);
    if (savedCart) {
      const cart = JSON.parse(savedCart) as Cart;
      // Recalculate totals to ensure data integrity
      return calculateCartTotals(cart, priceIncludesTax);
    }
  } catch (error) {
    console.error('Error loading cart from storage:', error);
  }
  return emptyCart(restaurantId);
};

const initialState: CartState = {
  cart: emptyCart(undefined),
  orders: [],
  status: 'idle',
  restaurant: undefined,
};

export const fetchRestaurantAndSetCart = createAsyncThunk<
  void,
  { restaurantId: string },
  { state: RootState }
>('cart/fetchRestaurantAndSetCart', async ({ restaurantId }, thunkApi) => {
  const { customerRestaurants } = thunkApi.getState();
  let restaurant = customerRestaurants.restaurants[restaurantId];

  if (!restaurant) {
    const fetchedRestaurantAction = await thunkApi.dispatch(
      fetchCachedRestaurantAsync(restaurantId)
    );
    if (fetchCachedRestaurantAsync.fulfilled.match(fetchedRestaurantAction)) {
      restaurant = fetchedRestaurantAction.payload;
    }
  }

  if (restaurant) {
    thunkApi.dispatch(setCartRestaurant(restaurant));
  }
});

export const createOrderAsync = createAsyncThunk<
  Order,
  { cart: Cart },
  { rejectValue: { error: unknown } }
>('orders/create', async ({ cart }, thunkApi) => {
  try {
    const restaurantId = cart.restaurantId;
    const restaurantName = cart.restaurantName;
    if (!restaurantId) {
      return thunkApi.rejectWithValue({ error: 'Restaurant ID is missing' });
    }
    const order = await api.Cart.create(restaurantId, cart);
    thunkApi.dispatch(
      setCustomerOrder({ restaurantId, restaurantName, order })
    );
    thunkApi.dispatch(clearCart(restaurantId));
    return order;
  } catch (error: unknown) {
    return thunkApi.rejectWithValue({ error });
  }
});

export const setCartRestaurantAsync = createAsyncThunk<
  Restaurant,
  Restaurant,
  { rejectValue: { error: unknown } }
>('cart/setRestaurant', async (restaurant, thunkApi) => {
  try {
    // Fetch restaurant details to get the default tax rate
    const restaurantDetails = await api.CustomerRestaurant.details(
      restaurant.id
    );
    thunkApi.dispatch(setCartRestaurant(restaurantDetails));
    return restaurantDetails;
  } catch (error: unknown) {
    return thunkApi.rejectWithValue({ error });
  }
});

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartRestaurant: (state, action: PayloadAction<Restaurant>) => {
      const restaurantPayload = action.payload;
      const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes

      if (restaurantPayload.id !== state.cart.restaurantId) {
        state.cart = loadCartFromStorage(restaurantPayload.id);
      }
      state.cart.restaurantName = restaurantPayload.name;
      state.cart.restaurantId = restaurantPayload.id;
      state.cart.defaultTaxRate = restaurantPayload.defaultTaxRate || 0;
      state.restaurant = restaurantPayload;

      // Save restaurant to local storage with timestamp
      const cachedRestaurant: CachedRestaurant = {
        restaurant: restaurantPayload,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        `restaurant_${restaurantPayload.id}`,
        JSON.stringify(cachedRestaurant)
      );

      updateCustomerRestaurant(
        restaurantPayload.id,
        restaurantPayload.name,
        state.cart
      );
    },
    setCustomerOrder: (state, action) => {
      const { restaurantId, restaurantName, order } = action.payload;
      order.restaurantName = restaurantName;
      order.restaurantId = restaurantId;
      if (!order || !order.id) {
        console.warn('Invalid order received in setCustomerOrder:', order);
        return;
      }
      if (!Array.isArray(state.orders)) {
        state.orders = [];
      }
      state.orders = replaceObjectById(state.orders, order.id, order);
      updateCustomerOrder(order);
    },
    updateProductQuantity: (
      state,
      action: PayloadAction<{
        cartItem: CartItem;
        quantity: number;
        restaurantId: string;
      }>
    ) => {
      const { cartItem, quantity, restaurantId } = action.payload;
      const itemIndex = state.cart.orderItems.findIndex(
        (item) => item.cartItemId === cartItem.cartItemId
      );

      if (itemIndex !== -1) {
        const existingItem = state.cart.orderItems[itemIndex];
        existingItem.quantity += quantity;
        if (existingItem.quantity <= 0) {
          state.cart.orderItems.splice(itemIndex, 1);
        } else {
          existingItem.totalPrice = calculateItemTotal(existingItem);
        }
      } else if (quantity > 0) {
        const newItem = { ...cartItem, quantity };
        newItem.totalPrice = calculateItemTotal(newItem);
        state.cart.orderItems.push(newItem);
      }

      state.cart = calculateCartTotals(
        state.cart,
        state.restaurant?.settings?.priceIncludesTax ?? false
      );

      // Save to localStorage
      localStorage.setItem(`cart_${restaurantId}`, JSON.stringify(state.cart));
      updateCustomerRestaurant(restaurantId, null, state.cart);
    },
    clearCart: (state, action: PayloadAction<string>) => {
      const restaurantId = action.payload;
      state.cart = emptyCart(restaurantId);
      // Remove from localStorage
      localStorage.removeItem(`cart_${restaurantId}`);
      updateCustomerRestaurant(restaurantId, null, state.cart);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createOrderAsync.pending, (state) => {
      state.status = 'pendingCreateRestaurant';
    });
    builder.addCase(createOrderAsync.fulfilled, (state, action) => {
      const order = action.payload;
      if (order) {
        state.orders = replaceObjectById(state.orders, order.id, order);
      }
      state.status = 'idle';
    });
    builder.addCase(createOrderAsync.rejected, (state) => {
      state.status = 'idle';
    });
  },
});

const calculateItemTotal = (cartItem: CartItem): number => {
  let total = cartItem.price;
  if (cartItem.customizations) {
    cartItem.customizations.forEach((customization) => {
      customization.options.forEach((option) => {
        total += option.price * option.quantity;
      });
    });
  }
  return total * cartItem.quantity;
};

export const {
  setCartRestaurant,
  updateProductQuantity,
  clearCart,
  setCustomerOrder,
} = cartSlice.actions;
